import { useMemo } from 'react'
import partition from 'lodash/partition'
import { accountManagementIsRoleDisabled } from 'domains/account-management/components/role-select/utils/AccountManagementRoleSelect.utils'
import { useSearchRolesQuery } from 'domains/account-management/api'
import { INTL_NON_EXISTENT_MESSAGE } from 'intl/intl.types'
import { useDomainIntl, type DomainMessages } from 'domains/account-management/utils/domain/intl'
import { type FormData } from 'domains/account-management/components/account-form/utils/AccountForm.types'
import { RoleRank } from './AccountManagementRoleSelect.types'

interface RoleSelectOptions {
  useDefaultOption?: boolean
  formData?: FormData
}

/**
 * Primary vs. Secondary Roles
 * - Primary Role: In general, users will only have a single primary role (e.g. Partner Admin, Staff, Manager).
 * This will give them a base set of permissions.
 * - Secondary Role: These roles define additional permissions/restrictions
 * (e.g. read-only role to restrict users from making changes)
 */
export const SECONDARY_ROLES = [
  { slug: 'legacy_read_only' },
  { slug: 'retailer_tools_oncall' },
  { slug: 'insights_portal' },
]

export const useAccountManagementRoles = (hookOptions: RoleSelectOptions) => {
  const { formData, useDefaultOption = true } = hookOptions
  const intl = useDomainIntl()
  const { data, loading, error } = useSearchRolesQuery()

  const options = useMemo(() => {
    const incomingOptions = !data
      ? []
      : data.roleSearch.map(role => {
          /**
           * Fallback for missing translations, role additions or unexpected name changes
           * attempt fetching from our map above, if it does not exist,
           * use the value as-is from the database.
           *
           * We use the placeholder __NON_EXISTENT_MESSAGE__ id, because otherwise
           * intl throws an error if you don't pass one.
           *
           * This will also have the side effect of logging an error.
           */
          const label = intl.formatMessage({
            defaultMessage: role.name,
            id: (`accountManagementDomain.roles.${role.slug}.name` ??
              INTL_NON_EXISTENT_MESSAGE) as DomainMessages,
          })

          return {
            label,
            description: intl.formatMessage({
              defaultMessage: role.description,
              id: (`accountManagementDomain.roles.${role.slug}.description` ??
                INTL_NON_EXISTENT_MESSAGE) as DomainMessages,
            }),
            value: role.id,
            slug: role.slug,
            isDisabled: false,
            roleRank: SECONDARY_ROLES.map(role => role.slug).includes(role.slug)
              ? RoleRank.Secondary
              : RoleRank.Primary,
          }
        })

    if (!useDefaultOption) return incomingOptions

    return [
      {
        label: intl.formatMessage({ id: 'accountManagementDomain.roles.all' }),
        description: '',
        value: undefined,
        slug: 'all-roles',
        isDisabled: false,
        roleRank: RoleRank.Primary,
      },
    ].concat(incomingOptions)
  }, [data, intl, useDefaultOption])

  const [primaryOptions, secondaryOptions] = useMemo(
    () =>
      partition(options, option => !SECONDARY_ROLES.map(role => role.slug).includes(option.slug)),
    [options]
  )
  formData &&
    options.forEach(option => {
      option.isDisabled = accountManagementIsRoleDisabled(option.slug, formData)
    })

  // Loop through existing form data and apply role rank to each option
  // In future we may want to address including primary/secondary roles when fetching data from back end
  formData &&
    formData.roles.forEach(
      role =>
        (role['roleRank'] = primaryOptions.some(option => option.value === role.value)
          ? RoleRank.Primary
          : RoleRank.Secondary)
    )
  // use the roleIsDisabledConfig to check disabled status of each role
  // this could not be done in the options useMemo because the useMemo would
  // not rerun when the form data changes
  formData &&
    secondaryOptions.forEach(option => {
      option.isDisabled = accountManagementIsRoleDisabled(option.slug, formData)
    })

  return {
    primaryOptions,
    secondaryOptions,
    loading,
    error,
  }
}
